import { CSSProperties, Fragment, useMemo } from 'react';
import {
  deserify,
  GeneoKatex,
  getTeacherSubjectName,
  PrintableQuestion,
} from '@geneo2-web/shared-ui';
import { useAppSelector } from '../../../../../reduxStore/reduxHooks';
import {
  MultiSubjectSection,
  MultiSubjectSubSection,
  SectionQuestionMetaData,
  TaskInfo,
} from '@protos/learning_management/lms.db_pb';
import { Question } from '@protos/content_management/content.db_pb';
import {
  formatMarksDisplay,
  getDisplayQuestionsForMeta,
  getGroupedQuestionHeaderBody,
  getSubSectionAttemptAnyInstruction,
  getSubSectionMarksTotal,
  isPassageSectionEnabled,
  toRomanNumeral,
} from '../../../utils/QPGv2.utils';
import { getCloudUploadPreviewSrc, getCloudUploadExportSrc } from '../../../../../utils/commonUtils';

const printableSx: CSSProperties = {
  breakInside: 'avoid',
  pageBreakInside: 'avoid',
};

// NOTE: Word (.docx) does not support flexbox / gap / absolute positioning.
// This preview is exported to DOCX, so layout relies on block flow, margins and
// tables instead of flex.
const stylesHtml: { [key: string]: CSSProperties } = {
  root: {
    padding: '10px 20px 10px',
    ...printableSx,
  },
  ellipsisText: {
    fontFamily: 'Poppins',
    textAlign: 'center',
    textTransform: 'uppercase',
    marginTop: '15px',
    marginBottom: 0,
    wordBreak: 'break-word',
  },
  sectionContainer: {
    marginTop: '5px',
  },
  section: {
    marginBottom: '40px',
  },
  subjectSectionDetails: {
    textAlign: 'center',
  },
  subSectionContainer: {},
  subsectionBlock: {
    marginBottom: '10px',
  },
  subsectionHeader: {
    width: '100%',
    paddingTop: '10px',
    paddingBottom: '10px',
    boxSizing: 'border-box',
  },
  subsectionDetailsTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '10px',
    ...printableSx,
  },
  detailsCellLeft: {
    textAlign: 'left',
    verticalAlign: 'middle',
    ...printableSx,
  },
  detailsCellRight: {
    textAlign: 'right',
    verticalAlign: 'middle',
    ...printableSx,
  },
  subsectionContent: {
    paddingTop: '10px',
  },
  questionsColumn: {},
  questionBlock: {},
  questionBlockWithAlts: {
    padding: '18px 13px',
  },
  instructionText: {
    fontFamily: 'Poppins',
    marginTop: '5px',
    marginBottom: '5px',
    fontSize: '16px',
    ...printableSx,
  },
  orBox: {
    padding: '10px 20px',
    textAlign: 'center',
    fontWeight: 'bold',
    ...printableSx,
  },
  passageBlock: {
    marginBottom: '16px',
    ...printableSx,
  },
  passageImageWrap: {
    textAlign: 'center',
    margin: '12px 0',
    ...printableSx,
  },
  passageImage: {
    maxWidth: '100%',
    height: 'auto',
    display: 'inline-block',
    ...printableSx,
  },
  passageText: {
    fontFamily: 'Poppins',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: '#1D1D1D',
    margin: 0,
    ...printableSx,
  },
  groupedChildIndent: {
    marginLeft: '40px',
  },
  iconImg: {
    width: 20,
    height: 20,
    display: 'inline-block',
    verticalAlign: 'middle',
    objectFit: 'contain',
    ...printableSx,
  },
  noContent: {
    padding: '20px',
    textAlign: 'center',
    color: '#666',
    fontFamily: 'Poppins',
    ...printableSx,
  },
};

interface IQPGPreviewHtml {
  printAnswer?: boolean;
  /**
   * Render questions with a table layout (keeps number/body/marks on one line in
   * Word). For PDF this is disabled so long questions can split across pages
   * instead of the whole question jumping to the next page.
   */
  useTableLayout?: boolean;
}

export const QPGPreviewHtml = (props: IQPGPreviewHtml) => {
  const { printAnswer, useTableLayout = false } = props;
  const ICONS_BASE = '/assets/shared-ui/icons';

  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const { qpg_content } = deserify(useAppSelector((state) => state.assessment));

  const sections = qpg_content?.multiSubjectSections ?? [];
  const assessmentContent = qpg_content?.assessmentContent ?? [];
  const questionTypeAndMarks = qpg_content?.questionTypeAndMarks ?? [];
  const questionsSequenceInfo = useMemo(
    () => new TaskInfo(qpg_content?.questionsSequenceInfo),
    [qpg_content?.questionsSequenceInfo]
  );
  const taskQuestionInfo = questionsSequenceInfo.taskQuestionInfo ?? [];

  // ----------------------------------------------------

  const questionMap = useMemo(() => {
    const map = new Map<string, Question>();
    assessmentContent.forEach((q) => {
      const question = new Question(q);
      if (question.questionId) map.set(String(question.questionId), question);
    });
    return map;
  }, [assessmentContent]);

  // ----------------------------------------------------

  const getMarksForQuestionId = (questionId: string): number => {
    const ques = questionMap.get(questionId);
    const taskInfo = taskQuestionInfo.find(
      (info) => info.questionId === questionId
    );
    const quesType = ques?.questionType;
    const foundQuesType = questionTypeAndMarks.find(
      (qType) => qType.questionType === quesType
    );
    if (foundQuesType) return foundQuesType.marks ?? 0;
    if (typeof taskInfo?.maxMarks === 'number') return taskInfo.maxMarks;
    const existing =
      ques?.question?.model?.value?.commonQuestionContent?.marks?.[0];
    if (typeof existing === 'number') return existing;
    return 0;
  };

  // ----------------------------------------------------

  const getFormattedQuestionFromId = (id: string): Question | undefined => {
    const ques = questionMap.get(id);
    return ques ?? undefined;
  };

  // ----------------------------------------------------

  const subsectionTotalMarks = (subSection: MultiSubjectSubSection): number =>
    getSubSectionMarksTotal({
      subSection,
      getMarksForQuestionId,
    });

  // ----------------------------------------------------

  const getQuestionNumberFromMultiSubject = (props: {
    sectionIndex: number;
    subSectionIndex: number;
    questionIndexInSubSection: number;
    multiSubjectSections: MultiSubjectSection[];
  }): number => {
    const {
      sectionIndex,
      subSectionIndex,
      questionIndexInSubSection,
      multiSubjectSections,
    } = props;
    let n = 0;
    for (let s = 0; s < sectionIndex; s++) {
      const section = multiSubjectSections[s];
      if (!section?.multiSubjectSubSections?.length) continue;
      for (const sub of section.multiSubjectSubSections) {
        n += sub.subSectionMetaData?.length ?? 0;
      }
    }
    const currentSection = multiSubjectSections[sectionIndex];
    if (currentSection?.multiSubjectSubSections) {
      for (let ss = 0; ss < subSectionIndex; ss++) {
        n +=
          currentSection.multiSubjectSubSections[ss]?.subSectionMetaData
            ?.length ?? 0;
      }
      n += questionIndexInSubSection;
    }
    return n + 1;
  };

  // ----------------------------------------------------

  const getQuestionsAndNumberForMeta = (params: {
    quesMeta: SectionQuestionMetaData;
    subSection: MultiSubjectSubSection;
    sectionIndex: number;
    subSectionIndex: number;
    index: number;
  }): { questions: Question[]; questionNumber: number } | null => {
    const { quesMeta, subSection, sectionIndex, subSectionIndex, index } =
      params;
    const allQuestionsMeta = getDisplayQuestionsForMeta(quesMeta);
    const questions = allQuestionsMeta
      .map((ques) => {
        const q = getFormattedQuestionFromId(ques.questionId);
        if (!q) return undefined;
        const subSectionMarks = subSection.questionTypeAndMarks?.find(
          (val) => val.questionType === q.questionType
        );
        if (q.question?.model?.value?.commonQuestionContent) {
          if (typeof ques.maxMarks === 'number') {
            q.question.model.value.commonQuestionContent.marks = [
              ques.maxMarks,
            ];
          } else if (subSectionMarks) {
            q.question.model.value.commonQuestionContent.marks = [
              subSectionMarks.marks ?? 0,
            ];
          } else {
            q.question.model.value.commonQuestionContent.marks = [
              getMarksForQuestionId(ques.questionId),
            ];
          }
          const taskInfo = taskQuestionInfo.find(
            (info) => info.questionId === ques.questionId
          );
          if (typeof taskInfo?.estimatedTimeInMin === 'number') {
            q.question.model.value.commonQuestionContent.time =
              taskInfo.estimatedTimeInMin * 60;
          }
        }
        return q;
      })
      .filter((q): q is Question => q != null);
    if (questions.length === 0) return null;
    const questionNumber = getQuestionNumberFromMultiSubject({
      sectionIndex,
      subSectionIndex,
      questionIndexInSubSection: index,
      multiSubjectSections: sections,
    });
    return { questions, questionNumber };
  };

  // ----------------------------------------------------

  const getTotalMarksOfSection = (section: MultiSubjectSection): number =>
    (section.multiSubjectSubSections ?? []).reduce(
      (sectionTotal, subSection) =>
        sectionTotal + subsectionTotalMarks(subSection),
      0
    );

  // ----------------------------------------------------

  const sectionLetter = (index: number) => String.fromCharCode(65 + index);
  const getSubjectName = (subjectId: number) =>
    getTeacherSubjectName(subjectId, user_info?.teachClassSubjects) ?? '';

  // ----------------------------------------------------

  if (!qpg_content || sections.length === 0) {
    return (
      <div style={stylesHtml.root}>
        <p style={{ fontFamily: 'Poppins', color: '#666' }}>
          No paper content to preview.
        </p>
      </div>
    );
  }

  const totalMarks = sections.reduce((total, section) => {
    return total + getTotalMarksOfSection(section);
  }, 0);

  return (
    <div style={stylesHtml.root}>
      {user_info?.schoolDetails[0]?.schoolName && (
        <div
          style={{
            color: '#000',
            textAlign: 'center',
            display: 'block',
            padding: '5px 0',
            fontWeight: 'bold',
            ...printableSx,
          }}
          className="avoid-break-element"
        >
          {user_info?.schoolDetails[0]?.schoolName}
        </div>
      )}
      <div style={{ ...printableSx }} className="avoid-break-element">
        {qpg_content?.assessment?.class && (
          <div
            style={{
              color: '#000',
              textAlign: 'center',
              display: 'block',
              fontWeight: 'bold',
              ...printableSx,
            }}
            className="avoid-break-element"
          >
            {qpg_content?.assessment?.class
              ? `GRADE ${qpg_content?.assessment?.class}`
              : ''}
          </div>
        )}
      </div>
      <h2 style={{ ...stylesHtml.ellipsisText, marginTop: '0' }}>
        {qpg_content?.assessment?.assessmentTitle}
      </h2>
      {totalMarks && totalMarks > 0 && (
        <div
          style={{
            color: '#000',
            textAlign: 'center',
            display: 'block',
            fontWeight: 'bold',
            ...printableSx,
          }}
          className="avoid-break-element"
        >
          Total Marks: {formatMarksDisplay(totalMarks)}
        </div>
      )}

      {qpg_content?.assessment?.assessmentInstructions &&
        qpg_content.assessment.assessmentInstructions.length > 0 &&
        qpg_content.assessment.assessmentInstructions.some(Boolean) && (
          <div
            style={stylesHtml.instructionText}
            dangerouslySetInnerHTML={{
              __html: new GeneoKatex(
                qpg_content.assessment.assessmentInstructions.join('')
              ).parsedHtml(),
            }}
          />
        )}

      <div style={stylesHtml.sectionContainer}>
        {sections.map((section, sectionIndex) => (
          <div
            key={`section-${sectionIndex}-${section.sectionName}`}
            style={stylesHtml.section}
          >
            <div style={stylesHtml.subjectSectionDetails}>
              {sections.length > 1 && (
                <>
                  <div
                    style={{
                      fontFamily: 'Poppins',
                      color: '#007CDC',
                      margin: 0,
                      fontWeight: 'bold',
                    }}
                  >
                    Section {sectionLetter(sectionIndex)} -{' '}
                    {getSubjectName(section.subjectId)}
                    {section.sectionTitle ? ` - ${section.sectionTitle}` : ''}
                  </div>

                  {/* <p
                    style={{
                      fontFamily: 'Poppins',
                      color: '#0AA34F',
                      fontWeight: 'bold',
                      marginLeft: 'auto',
                      marginTop: '15px',
                      marginBottom: 0,
                    }}
                  >
                    Total: {getTotalMarksOfSection(section)} Marks
                  </p> */}
                </>
              )}

              {section.instructions?.length > 0 &&
                section.instructions.some(Boolean) && (
                  <p style={stylesHtml.instructionText}>
                    {section.instructions.join('\n')}
                  </p>
                )}
            </div>

            <div style={stylesHtml.subSectionContainer}>
              {!section.multiSubjectSubSections?.length ? (
                <div style={stylesHtml.noContent}>No Questions Added</div>
              ) : (
                (section.multiSubjectSubSections ?? []).map(
                  (subSection, subSectionIndex) => (
                    <div
                      key={`sub-${sectionIndex}-${subSectionIndex}-${subSection.subSectionName}`}
                      style={stylesHtml.subsectionBlock}
                    >
                      <div style={stylesHtml.subsectionHeader}>
                        <div style={{ width: '100%' }}>
                          {(section.multiSubjectSubSections ?? []).length >
                            1 && (
                            <div
                              style={{
                                fontFamily: 'Poppins',
                                margin: 0,
                                textAlign: 'center',
                                fontWeight: 'bold',
                                textDecoration: 'underline',
                              }}
                            >
                              {subSection.subSectionName}
                            </div>
                          )}
                          <table
                            border={0}
                            style={stylesHtml.subsectionDetailsTable}
                          >
                            <tbody>
                              <tr>
                                <td style={stylesHtml.detailsCellLeft}>
                                  <p style={{ margin: 0 }}>
                                    <img
                                      src={`${ICONS_BASE}/questions.png`}
                                      alt=""
                                      aria-hidden
                                      style={stylesHtml.iconImg}
                                    />
                                    <span style={{ fontFamily: 'Poppins' }}>
                                      {' '}
                                      Total No of Questions:{' '}
                                      <b>
                                        {subSection.subSectionMetaData
                                          ?.length ?? 0}
                                      </b>
                                    </span>
                                  </p>
                                </td>
                                <td style={stylesHtml.detailsCellRight}>
                                  <p style={{ margin: 0 }}>
                                    <img
                                      src={`${ICONS_BASE}/percentage-primary.png`}
                                      alt=""
                                      aria-hidden
                                      style={stylesHtml.iconImg}
                                    />
                                    <span style={{ fontFamily: 'Poppins' }}>
                                      {' '}
                                      Marks:{' '}
                                      <b>
                                        {formatMarksDisplay(
                                          subsectionTotalMarks(subSection)
                                        )}
                                      </b>
                                    </span>
                                  </p>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div style={stylesHtml.subsectionContent}>
                        {(() => {
                          const isPassage = isPassageSectionEnabled(subSection);
                          const passageUpload =
                            subSection.passageImagesUpload?.[0];
                          const passageImageSrc =
                            getCloudUploadPreviewSrc(passageUpload);
                          const passageExportSrc =
                            getCloudUploadExportSrc(passageUpload);
                          const passageBody = (subSection.passageText ?? [])
                            .map((t) => t?.trim())
                            .filter(Boolean)
                            .join('\n\n');
                          if (
                            !isPassage ||
                            (!passageImageSrc && !passageBody)
                          ) {
                            return null;
                          }
                          return (
                            <div style={stylesHtml.passageBlock}>
                              {passageBody ? (
                                <p style={stylesHtml.passageText}>
                                  {passageBody}
                                </p>
                              ) : null}
                              {passageImageSrc ? (
                                <p style={stylesHtml.passageImageWrap}>
                                  <img
                                    src={passageImageSrc}
                                    alt="Passage"
                                    style={stylesHtml.passageImage}
                                    {...(passageExportSrc
                                      ? { 'data-export-src': passageExportSrc }
                                      : {})}
                                  />
                                </p>
                              ) : null}
                            </div>
                          );
                        })()}
                        <div style={stylesHtml.questionsColumn}>
                          {subSection.attemptAnyCount !== undefined ? (
                            <p
                              style={{
                                margin: '0 0 8px 0',
                                fontFamily: 'Poppins',
                                fontWeight: 600,
                              }}
                            >
                              {getSubSectionAttemptAnyInstruction(
                                subSection.attemptAnyCount
                              )}
                            </p>
                          ) : null}
                          {(subSection.subSectionMetaData ?? [])
                            .sort(
                              (a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0)
                            )
                            .map((quesMeta, index) => {
                              const result = getQuestionsAndNumberForMeta({
                                quesMeta,
                                subSection,
                                sectionIndex,
                                subSectionIndex,
                                index,
                              });
                              if (!result) return null;
                              const { questions, questionNumber } = result;
                              const hasAlts = questions.length > 1;
                              const isGrouped = !!quesMeta.isGrouped;
                              const groupedQuestionType =
                                quesMeta.questionType ??
                                questions[0]?.questionType;
                              return (
                                <div
                                  key={quesMeta.questionId}
                                  style={
                                    hasAlts && !isGrouped
                                      ? stylesHtml.questionBlockWithAlts
                                      : stylesHtml.questionBlock
                                  }
                                >
                                  {isGrouped ? (
                                    <PrintableQuestion
                                      number={questionNumber}
                                      headerOnlyText={getGroupedQuestionHeaderBody(
                                        {
                                          meta: quesMeta,
                                          questionType: groupedQuestionType,
                                        }
                                      )}
                                      // Table layout keeps the group header atomic in
                                      // PDF (flex + page-break-inside:avoid is unreliable).
                                      useTableLayout={
                                        useTableLayout || isGrouped
                                      }
                                      splittable={false}
                                    />
                                  ) : null}
                                  {questions.map((question, altIdx) => {
                                    const printable = (
                                      <PrintableQuestion
                                        ques={question}
                                        number={
                                          isGrouped
                                            ? toRomanNumeral(altIdx + 1)
                                            : altIdx === 0
                                            ? questionNumber
                                            : undefined
                                        }
                                        marks={
                                          isGrouped || altIdx === 0
                                            ? question.question?.model?.value
                                                ?.commonQuestionContent
                                                ?.marks?.[0]
                                            : undefined
                                        }
                                        showAnswer={printAnswer}
                                        isAiGenerated={question?.isAiGenerated}
                                        showMedia={true}
                                        // Grouped children: always table so each
                                        // Q.i… stays one page-break unit in PDF.
                                        useTableLayout={
                                          useTableLayout || isGrouped
                                        }
                                        splittable={
                                          !useTableLayout && !isGrouped
                                        }
                                      />
                                    );
                                    return (
                                      <Fragment key={question.questionId}>
                                        {isGrouped ? (
                                          <div
                                            className="avoid-break-element"
                                            style={{
                                              ...stylesHtml.groupedChildIndent,
                                              ...printableSx,
                                            }}
                                          >
                                            {printable}
                                          </div>
                                        ) : (
                                          printable
                                        )}
                                        {altIdx < questions.length - 1 &&
                                          !quesMeta.isGrouped && (
                                            <div style={stylesHtml.orBox}>
                                              OR
                                            </div>
                                          )}
                                      </Fragment>
                                    );
                                  })}
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  )
                )
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
